// Cross-tab coordination over BroadcastChannel (plan item 5). Two jobs:
//   • track which game boards this tab has open, and
//   • when a ready/accept/turn navigation is about to open a board, first ask
//     the other tabs — if one already has it open, focus that tab instead of
//     opening a duplicate here.
// Best-effort: where BroadcastChannel is unavailable or no tab answers within
// the grace, it falls back to normal navigation. Router-free (navigation is
// passed in as a callback) so it unit-tests with a mock channel.

interface WhoHas {
  type: "who-has";
  gameId: string;
  nonce: string;
}
interface IHave {
  type: "i-have";
  gameId: string;
  nonce: string;
}
interface Focus {
  type: "focus";
  gameId: string;
}
export type TabMsg = WhoHas | IHave | Focus;

const CHANNEL = "chess-tabs";
/** How long to wait for another tab to claim a board before navigating here. */
const ANSWER_MS = 200;

let bc: BroadcastChannel | undefined;
const openBoards = new Map<string, () => void>(); // gameId → local focus handler
const pending = new Map<string, (has: boolean) => void>(); // nonce → resolver

function channel(): BroadcastChannel | undefined {
  if (bc) return bc;
  if (typeof BroadcastChannel === "undefined") return undefined;
  bc = new BroadcastChannel(CHANNEL);
  bc.onmessage = (e: MessageEvent) => onMessage(e.data as TabMsg);
  return bc;
}

function onMessage(msg: TabMsg): void {
  const ch = channel();
  if (!ch) return;
  switch (msg.type) {
    case "who-has":
      // Answer only if *this* tab holds the board.
      if (openBoards.has(msg.gameId)) ch.postMessage({ type: "i-have", gameId: msg.gameId, nonce: msg.nonce });
      break;
    case "i-have": {
      const resolve = pending.get(msg.nonce);
      if (resolve) {
        pending.delete(msg.nonce);
        resolve(true);
      }
      break;
    }
    case "focus":
      openBoards.get(msg.gameId)?.();
      break;
  }
}

/** Register that this tab has `gameId` open; `onFocus` runs when another tab asks
 *  to focus it. Returns an unregister fn (call on unmount). */
export function registerBoard(gameId: string, onFocus: () => void): () => void {
  openBoards.set(gameId, onFocus);
  channel(); // start listening so `who-has` queries get answered
  return () => {
    openBoards.delete(gameId);
  };
}

/** Ask whether any other tab has `gameId` open. Resolves `false` if none answers
 *  within the grace (or there's no channel). Exposed for tests. */
export async function anyTabHasBoard(gameId: string): Promise<boolean> {
  const ch = channel();
  if (!ch) return false;
  const nonce = makeNonce();
  return new Promise<boolean>((resolve) => {
    pending.set(nonce, resolve);
    setTimeout(() => {
      if (pending.delete(nonce)) resolve(false);
    }, ANSWER_MS);
    ch.postMessage({ type: "who-has", gameId, nonce });
  });
}

/** Open `gameId`, but prefer an existing tab: if this tab already has it, focus
 *  locally; if another tab does, tell it to focus; otherwise run `onNavigate`. */
export async function focusOrNavigate(gameId: string, onNavigate: () => void): Promise<void> {
  const mine = openBoards.get(gameId);
  if (mine) {
    mine();
    return;
  }
  const ch = channel();
  if (!ch) {
    onNavigate();
    return;
  }
  if (await anyTabHasBoard(gameId)) ch.postMessage({ type: "focus", gameId });
  else onNavigate();
}

function makeNonce(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

/** Test-only: drop all registrations, pending queries, and the channel. */
export function __resetTabs(): void {
  openBoards.clear();
  pending.clear();
  bc?.close();
  bc = undefined;
}
